<template>
    <div v-if="loading||data">
        <v-layout row wrap v-if="data">
            <v-flex xs11>
                <vue-echart :white-mode="whiteMode" :config="ecConfig" :height="height"
                            ></vue-echart>
            </v-flex>
            <v-flex xs-1 class="pa-3">
                <v-icon v-if="!interval" class="cursor-as-pointer" @click="play()">fa-play</v-icon>
                <v-icon v-if="interval" class="cursor-as-pointer" @click="pause()">fa-pause</v-icon>
            </v-flex>
        </v-layout>
        <loading-progress-bar v-if="loading" :loading="loading" color="secondary"></loading-progress-bar>
    </div>
</template>

<script>
import LoadingProgressBar from "../LoadingProgressBar";
import {callAPI} from '@/helper'
import data3Processor from "../mixins/data3Processor";
import VueEchart from "../VueEchart.vue";

export default {
    props: {
        parameters: {type: Object, default: null},
        equipment: {type: String, default: null},
        title: {type: String, default: "Optimization"},
        dateRange: {type: Object, default: null},
        data3query: {
            type: Object, default: null
        },
        target: {type: String, default: null},
        whiteMode: {type: Boolean, default: false},
        height: {type: Number, default: 490},
        increment: {type: Number, default: 96},
    },
    components: {LoadingProgressBar, VueEchart},
    mixins: [data3Processor],
    computed: {
        ecConfig() {
            if (!this.data || !this.target) {
                return null
            }
            let data = JSON.parse(JSON.stringify(this.data))
            let fields = Object.keys(this.data).filter(x => x !== 'timestamp' && x !== this.target)
            fields.sort()
            let names = [...fields, this.target]
            let parallelData = []
            for (let index in data.timestamp) {
                if (index <= this.frameIndex * this.increment) {
                    if (data[this.target][index] !== null) {
                        let line = []
                        for (let name of names) {
                            line.push(data[name][index])
                        }
                        parallelData.push(line)
                    }
                } else {
                    break
                }
            }
            return {
                title: {
                    text: this.title,
                    left: 'center'
                },
                parallelAxis: names.map((item, index) => {
                    return {dim: index, name: item}
                }),
                visualMap: {
                    show: false,
                    dimension: names.length - 1,
                    min:Math.min(...parallelData.map(x=>x[names.length-1])),
                    max:Math.max(...parallelData.map(x=>x[names.length-1])),
                    calculable: true,
                },
                series: {
                    type: 'parallel',
                    data: parallelData
                }
            }
        }
    },
    name: 'OptimizationVizWidget',
    data: function () {
        return {
            data: null,
            loading: false,
            reloadInc: 0,
            frameIndex: 1,
            maxFrames: 1,
            timeout: null,
            interval: null,
        }
    },
    beforeDestroy: function () {
        clearInterval(this.interval)
    },
    watch: {
        dateRange() {
            if (this.data3query) {
                this.refresh()
            }
        },
        equipment() {
            if (this.data3query) {
                this.refresh()
            }
        },
        parameters: {
            handler() {
                if (this.data3query) {
                    this.refresh()
                }
            },
            deep: true,
            immediate: true,
        },
    },
    mounted: function () {
        this.refresh()
    },
    methods: {
        incPlay() {
            if (this.frameIndex < this.maxFrames) {
                this.frameIndex += 1;
            } else {
                clearInterval(this.interval)
                this.interval = null
            }
        },
        play() {
            let me = this
            if (this.frameIndex >= this.maxFrames) {
                this.frameIndex=1
            }
            me.interval = setInterval(function () {
                me.incPlay()
            }, 500);
        },
        pause() {
            clearInterval(this.interval)
            this.interval = null
        },
        debounce(callback, wait) {
            clearTimeout(this.timeout);
            this.timeout = setTimeout(() => callback(), wait);
        },
        refresh() {
            this.debounce(this.getData, 500);
        },
        async getData() {
            clearInterval(this.interval)
            this.frameIndex = 1
            this.loading = true
            this.data = null
            let data = {}
            this.reloadInc = this.reloadInc + 1
            let currentInc = this.reloadInc
            if (this.data3query) {
                let payloadArray = JSON.parse(JSON.stringify(this.data3query['data-payload']))
                payloadArray = payloadArray.filter(x => x.filters && (x.filters.sensor_name || x.filters.equipment_name))
                for (let payload of payloadArray) {
                    payload.parameters = this.parameters
                    this.processDateRange(payload, this.dateRange)
                    if (!payload.filters.facility_name && this.$store.state.pointsListStore.pointsList.facility_name) {
                        payload.filters.facility_name = this.$store.state.pointsListStore.pointsList.facility_name
                    }
                    if (this.equipment) {
                        this.processEquipment(payload, this.equipment)
                    }
                    if (payload.onOnly) {
                        if (!payload.pipeline) {
                            payload.pipeline = []
                        }
                        payload.pipeline.unshift(
                            {
                                "type": "filter",
                                "filter_op": "eq",
                                "filter_by": "* - on",
                                "filter_value": 1,
                            }
                        )
                    }
                }
                let promises = payloadArray.map(payload => callAPI('data3/', payload))
                let results = await Promise.all(promises)
                if (currentInc !== this.reloadInc) {
                    return
                }
                for (let result of results) {
                    for (let seriesIndex in result.data) {
                        if (!data[seriesIndex]) {
                            data[seriesIndex] = result.data[seriesIndex]
                        }
                    }
                }
                if (Object.keys(data).length < 2) {
                    this.loading = false
                    return
                }
                this.maxFrames = Math.round(data.timestamp.length / this.increment) + 1
                this.frameIndex=this.maxFrames
                this.data = data
            }
            this.loading = false
        }
    }
}

</script>

<style scoped>

</style>
